import {
  Alert,
  AlertIcon,
  Box,
  Divider,
  Flex,
  Skeleton,
  Text,
  useToast,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useState } from 'react';

import { CreditAgreement } from '@finley-technologies/finley-types';

import { WipConfig } from '../../types';
import {
  parseCreditAgreementFullArtifact,
  getEligibilityKeys,
  getConcentrationLimitKeys,
} from '../common';
import { CalcAgentConfigEntity } from '../entity/CalcAgentConfigEntity';
import { LoanProduct } from '../property/LoanProduct';
import { LoanProductSelect } from 'packages/common-frontend/components/LoanProductSelect';
import { StickyHeaderSection } from 'packages/common-frontend/components/StickyHeaderSection';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { AddButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/add/AddButton';
import { AddPropertyModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/add/AddPropertyModal';
import { CreditAgreementSectionAccordionTable } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/CreditAgreementSectionAccordionTable';
import { MultiLoanProductConcentrationLimits } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/MultiLoanProductConcentrationLimits';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

const basePropertyKey = 'borrowingBaseMechanics';

type BorrowingBaseMechanicsProps = {
  borrowingBaseMechanics: CreditAgreement.DomainEntity.BorrowingBaseMechanicsWithEntities;
  creditFacilityId: string;
  wipConfig?: WipConfig;
  showReceivableAdvanceRateTreeConfig?: boolean;
};
export const BorrowingBaseMechanics = ({
  borrowingBaseMechanics,
  creditFacilityId,
  wipConfig,
  showReceivableAdvanceRateTreeConfig,
}: BorrowingBaseMechanicsProps) => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const loanProducts =
    borrowingBaseMechanics.calcAgentMechanics?.loanProductMechanics || [];
  const [currentLoanProductId, setCurrentLoanProductId] = useState(
    loanProducts.length ? loanProducts[0].entity.id : null,
  );

  const {
    data: currentCreditAgreementFullArtifactData,
    loading: isCurrentCreditAgreementFullArtifactLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { creditFacilityId },
  });

  if (
    !currentCreditAgreementFullArtifactData ||
    isCurrentCreditAgreementFullArtifactLoading
  ) {
    return <Skeleton height="600px" />;
  }

  let creditAgreement:
    | CreditAgreement.DomainEntity.CreditAgreementFullArtifact
    | undefined
    | null;

  if (
    currentCreditAgreementFullArtifactData?.currentCreditAgreementFullArtifact &&
    currentCreditAgreementFullArtifactData?.currentCreditAgreementFullArtifact
      .creditAgreement
  ) {
    creditAgreement = parseCreditAgreementFullArtifact(
      currentCreditAgreementFullArtifactData.currentCreditAgreementFullArtifact
        .creditAgreement,
    );
  }

  if (!creditAgreement) {
    return <Skeleton height="600px" />;
  }

  const {
    borrowingBaseMechanics: {
      calcAgentMechanics: {
        loanProductMechanics: currentCommittedLoanProductMechanics,
        multiLoanProductConcentrationLimits: currentCommittedMultiLoanProductConcentrationLimits,
      },
    },
  } = creditAgreement;
  const currentCommittedEligibilityTestsKeys = getEligibilityKeys(
    currentCommittedLoanProductMechanics,
  );
  const currentCommittedConcentrationLimitKeys = getConcentrationLimitKeys({
    loanProductMechanics: currentCommittedLoanProductMechanics,
    multiLoanProductConcentrationLimits: currentCommittedMultiLoanProductConcentrationLimits,
  });

  const sanitizedProperties = _.omit(
    borrowingBaseMechanics,
    'calcAgentMechanics',
  );

  const artifactDocumentId =
    borrowingBaseMechanics.calcAgentMechanics?._artifactDocumentId_ ||
    'NOT_SET';

  const allLoanProductOptions = loanProducts.map(({ entity }) => {
    return {
      value: entity.id,
      label: entity.name,
    };
  });

  const multiLoanProductConcentrationLimits =
    borrowingBaseMechanics.calcAgentMechanics
      .multiLoanProductConcentrationLimits;

  const isClientWipMode = wipConfig?.operationMode === 'client';

  const isValleyBankLenderView = flags.isValleyBankLenderView;

  return (
    <Box mt="40px">
      <StickyHeaderSection
        labelText={!isClientWipMode ? 'Borrowing Base Mechanics' : undefined}
      >
        {/* TODO @self-serve: temporary difference between lender and admin views as we migrate some borrowing base mechanics properties to more appropriate sections of the credit agreement */}
        {!isClientWipMode && (
          <>
            <Box>
              <Alert status="success" variant="subtle">
                <AlertIcon />
                artifactDocumentId = {artifactDocumentId}
              </Alert>
              <Divider mt="20px" />

              <Flex alignItems="center" mb="2">
                <Text fontWeight="bold" fontSize="20px">
                  Properties
                </Text>
                {wipConfig?.isMutateAllowed(basePropertyKey) && (
                  <>
                    <AddButton setIsOpen={setIsOpen} />
                    <AddPropertyModal
                      isOpen={isOpen}
                      setIsOpen={setIsOpen}
                      wipConfig={wipConfig}
                      basePropertyKey={basePropertyKey}
                      propertyMap={getIn(
                        wipConfig.creditAgreementFullArtifactPropertyMap,
                        basePropertyKey,
                      )}
                    />
                  </>
                )}
              </Flex>
            </Box>
            <CreditAgreementSectionAccordionTable
              properties={sanitizedProperties}
              basePropertyKey={basePropertyKey}
              wipConfig={wipConfig}
            />
          </>
        )}

        {!isValleyBankLenderView && (
          <Box mt="20px">
            <Flex alignItems="center" mb="2" mt="2">
              <Text fontWeight="bold" fontSize="20px">
                Calc Agent Options
              </Text>
            </Flex>
            <CalcAgentConfigEntity
              basePropertyKey={`${basePropertyKey}.calcAgentMechanics._config_`}
              currentConfig={borrowingBaseMechanics.calcAgentMechanics._config_}
              wipConfig={wipConfig}
            />
          </Box>
        )}

        <Box mt="20px">
          {!isValleyBankLenderView && (
            <Flex alignItems="center" mt="32px" mb="24px">
              <Text fontWeight="bold" fontSize="20px">
                Loan Products
              </Text>
            </Flex>
          )}
          {loanProducts && !!loanProducts.length ? (
            <Box>
              <LoanProductSelect
                creditFacilityId={creditFacilityId}
                loanProductId={currentLoanProductId}
                setLoanProductId={setCurrentLoanProductId}
              />
              {(() => {
                const loanProductIndex = _.findIndex(
                  loanProducts,
                  (lp) => lp.entity.id === currentLoanProductId,
                );

                const loanProductMechanics = loanProducts[loanProductIndex];

                return (
                  <LoanProduct
                    creditFacilityId={creditFacilityId}
                    loanProductMechanics={loanProductMechanics}
                    wipConfig={wipConfig}
                    key={currentLoanProductId}
                    basePropertyKey={`${basePropertyKey}.calcAgentMechanics.loanProductMechanics`}
                    idx={loanProductIndex}
                    // @MLP Advance Rate: can likely remove this prop
                    // once we have proper support for MLP Advance Rates
                    loanProductOptions={allLoanProductOptions}
                    currentCommittedConcentrationLimitKeys={
                      currentCommittedConcentrationLimitKeys
                    }
                    currentCommittedEligibilityTestsKeys={
                      currentCommittedEligibilityTestsKeys
                    }
                    showReceivableAdvanceRateTreeConfig={
                      showReceivableAdvanceRateTreeConfig
                    }
                    isValleyBankLenderView={isValleyBankLenderView}
                    multiLoanProductConcentrationLimits={
                      multiLoanProductConcentrationLimits
                    }
                  />
                );
              })()}
            </Box>
          ) : (
            <Text>There are no loan products configured</Text>
          )}
        </Box>

        {!isValleyBankLenderView && (
          <MultiLoanProductConcentrationLimits
            multiLoanProductConcentrationLimits={
              multiLoanProductConcentrationLimits
            }
            basePropertyKey={`${basePropertyKey}.calcAgentMechanics.multiLoanProductConcentrationLimits`}
            wipConfig={wipConfig}
            loanProductOptions={allLoanProductOptions}
            loanProducts={loanProducts}
            currentCommittedConcentrationLimitKeys={
              currentCommittedConcentrationLimitKeys
            }
          />
        )}
      </StickyHeaderSection>
    </Box>
  );
};
